// Budget. Real spending for one trip: create, read, update, delete against
// /trips/:tripId/budget, plus the CSV export.
//
// TWO RULES THIS SCREEN EXISTS TO KEEP:
// 1. Money is an INTEGER in minor units on the wire. A typed amount is
//    parsed by string arithmetic in lib/money.ts and never by parseFloat,
//    so nothing a traveller types can arrive at the server as 0.30000000004.
// 2. Totals are per currency and never added together. The app holds no
//    exchange rate, and a number it cannot derive it may not invent.
//
// The trip picker writes the app's active trip, so what is chosen here is
// the same trip Beranda, the map, Itinerary and Stats are looking at.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, IconPen, IconPlus, IconTrash, IconUpload, IconWallet } from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { apiClient, apiIssueMessage } from "../lib/api";
import { formatDateShort, formatNumber } from "../lib/format";
import { CURRENCIES, formatMoneyMinor, minorToInput, parseAmountToMinor } from "../lib/money";
import { navigate } from "../lib/router";
import {
  BUDGET_CATEGORIES,
  type BudgetCategory,
  type BudgetItem,
  type BudgetTotals,
} from "../lib/types";
import { useApp } from "../state/app";

type LoadState = "loading" | "ready" | "unavailable" | "failed";

interface Draft {
  id: string | null;
  category: BudgetCategory;
  label: string;
  amount: string;
  currency: string;
  spentAt: string;
  note: string;
}

function todayInput(): string {
  const now = new Date();
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function blankDraft(spentAt: string): Draft {
  return {
    id: null,
    category: "transport",
    label: "",
    amount: "",
    currency: "IDR",
    spentAt,
    note: "",
  };
}

export function BudgetScreen() {
  const { t, locale, trips, activeTrip, activeTripId, setActiveTripId } = useApp();

  const [items, setItems] = useState<BudgetItem[]>([]);
  const [totals, setTotals] = useState<BudgetTotals[]>([]);
  const [load, setLoad] = useState<LoadState>("loading");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (tripId: string) => {
    setLoad("loading");
    try {
      const summary = await apiClient.listBudget(tripId);
      if (summary === null) {
        setItems([]);
        setTotals([]);
        setLoad("unavailable");
        return;
      }
      setItems(summary.items);
      setTotals(summary.totals);
      setLoad("ready");
    } catch {
      setItems([]);
      setTotals([]);
      setLoad("failed");
    }
  }, []);

  useEffect(() => {
    if (!activeTripId) {
      setLoad("ready");
      return;
    }
    void reload(activeTripId);
  }, [activeTripId, reload]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => b.spentAt.localeCompare(a.spentAt)),
    [items],
  );

  async function submit(): Promise<void> {
    if (!activeTripId || !draft) return;
    const amountMinor = parseAmountToMinor(draft.amount, draft.currency);
    if (amountMinor === null || !draft.label.trim()) {
      setError(t("budget.amountInvalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        category: draft.category,
        label: draft.label.trim().slice(0, 120),
        amountMinor,
        currency: draft.currency,
        spentAt: draft.spentAt,
        note: draft.note.trim() || null,
      };
      if (draft.id) {
        const updated = await apiClient.updateBudgetItem(draft.id, payload);
        setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await apiClient.createBudgetItem(activeTripId, payload);
        setItems((current) => [created, ...current]);
      }
      setDraft(null);
      await reload(activeTripId);
    } catch (failure) {
      // The server's own words when it gave any: they name the field and the
      // reason, which is the only part a traveller can act on.
      setError(apiIssueMessage(failure) ?? t("budget.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    if (!activeTripId) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.deleteBudgetItem(id);
      setItems((current) => current.filter((item) => item.id !== id));
      await reload(activeTripId);
    } catch (failure) {
      setError(apiIssueMessage(failure) ?? t("budget.deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv(): Promise<void> {
    if (!activeTripId) return;
    setBusy(true);
    setError(null);
    try {
      const file = await apiClient.budgetCsv(activeTripId);
      if (!file) {
        setError(t("budget.exportUnavailable"));
        return;
      }
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      // The server names the file, and the server writes it: RFC 4180 with
      // CRLF and correct escaping, so a label holding a comma or a quote
      // round trips. The client never builds a CSV by hand.
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError(t("budget.exportUnavailable"));
    } finally {
      setBusy(false);
    }
  }

  if (!activeTrip) {
    return (
      <div className="screen-pad">
        <EmptyState
          glyph={<IconWallet />}
          title={t("budget.noTrip")}
          hint={t("budget.noTripHint")}
          action={
            <button type="button" className="btn btn-quiet" onClick={() => navigate("trips")}>
              {t("trips.title")}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="screen-pad">
      <div className="setting-list">
        <label className="setting-row">
          <span className="setting-label">{t("budget.trip")}</span>
          <select
            className="select"
            value={activeTripId ?? ""}
            onChange={(event) => setActiveTripId(event.target.value)}
          >
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.title}
              </option>
            ))}
          </select>
        </label>
      </div>

      {load === "unavailable" ? <p className="field-note">{t("budget.unavailable")}</p> : null}
      {load === "failed" ? <p className="field-note">{t("budget.loadFailed")}</p> : null}
      {load === "loading" ? <p className="field-note">{t("common.loading")}</p> : null}
      {error ? <p className="field-note">{error}</p> : null}

      {totals.length > 0 ? (
        <section className="section">
          <h2 className="section-title">{t("budget.total")}</h2>
          <div className="tile-grid tile-grid-duo">
            {totals.map((total) => (
              <div className="tile" key={total.currency}>
                <span className="tile-label">{total.currency}</span>
                <span className="tile-value tile-value-sm">
                  {formatMoneyMinor(total.currency, total.totalMinor, locale)}
                </span>
              </div>
            ))}
            <div className="tile">
              <span className="tile-label">{t("budget.itemCount")}</span>
              <span className="tile-value tile-value-sm">{formatNumber(items.length, locale)}</span>
            </div>
            {totals.length % 2 === 0 ? (
              <div className="tile">
                <span className="tile-label">{t("budget.byCategory")}</span>
                <span className="tile-value tile-value-sm">
                  {formatNumber(
                    new Set(items.map((item) => item.category)).size,
                    locale,
                  )}
                </span>
              </div>
            ) : null}
          </div>
          <p className="field-note">{t("budget.perCurrency")}</p>

          <div className="who-list">
            {totals.map((total) =>
              BUDGET_CATEGORIES.filter((category) => (total.byCategory[category] ?? 0) !== 0).map(
                (category) => (
                  <div className="who-row who-row-static" key={`${total.currency}-${category}`}>
                    <span className="who-text">
                      <span className="who-name">{t(`budgetCategory.${category}`)}</span>
                      <span className="who-meta">
                        {formatMoneyMinor(total.currency, total.byCategory[category] ?? 0, locale)}
                      </span>
                    </span>
                  </div>
                ),
              ),
            )}
          </div>
        </section>
      ) : null}

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={load !== "ready" || busy}
          onClick={() => setDraft(draft ? null : blankDraft(todayInput()))}
        >
          <Icon size={18} color="var(--color-primary-contrast)">
            <IconPlus />
          </Icon>
          {t("budget.addTitle")}
        </button>
        <button
          type="button"
          className="btn btn-quiet"
          disabled={load !== "ready" || busy || items.length === 0}
          onClick={() => void exportCsv()}
        >
          <Icon size={18}>
            <IconUpload />
          </Icon>
          {t("budget.export")}
        </button>
      </div>

      {draft ? (
        <section className="card">
          <h2 className="card-title">{draft.id ? t("budget.editTitle") : t("budget.addTitle")}</h2>

          <label className="field">
            <span className="field-label">{t("budget.label")}</span>
            <input
              className="input"
              value={draft.label}
              maxLength={120}
              placeholder={t("budget.labelPlaceholder")}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
            />
          </label>

          <div className="field-pair">
            <label className="field-sub">
              <span className="field-label-sm">{t("budget.amount")}</span>
              <input
                className="input"
                inputMode="decimal"
                value={draft.amount}
                onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
              />
            </label>
            <label className="field-sub">
              <span className="field-label-sm">{t("budget.currency")}</span>
              <select
                className="select select-block"
                value={draft.currency}
                onChange={(event) => setDraft({ ...draft, currency: event.target.value })}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="field-note">{t("budget.amountHint")}</p>

          <div className="field-pair">
            <label className="field-sub">
              <span className="field-label-sm">{t("budget.category")}</span>
              <select
                className="select select-block"
                value={draft.category}
                onChange={(event) =>
                  setDraft({ ...draft, category: event.target.value as BudgetCategory })
                }
              >
                {BUDGET_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`budgetCategory.${category}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-sub">
              <span className="field-label-sm">{t("budget.date")}</span>
              <input
                className="input"
                type="date"
                value={draft.spentAt}
                onChange={(event) => setDraft({ ...draft, spentAt: event.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span className="field-label">{t("budget.note")}</span>
            <input
              className="input"
              value={draft.note}
              maxLength={200}
              onChange={(event) => setDraft({ ...draft, note: event.target.value })}
            />
          </label>

          <div className="row-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !draft.label.trim() || !draft.amount.trim()}
              onClick={() => void submit()}
            >
              {draft.id ? t("budget.saveItem") : t("budget.addItem")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setDraft(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {load === "ready" && items.length === 0 && !draft ? (
        <EmptyState
          glyph={<IconWallet />}
          title={t("budget.nothingYet")}
          hint={t("budget.nothingYetHint")}
          action={
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => setDraft(blankDraft(todayInput()))}
            >
              <Icon size={18}>
                <IconPlus />
              </Icon>
              {t("budget.addTitle")}
            </button>
          }
        />
      ) : null}

      {sorted.length > 0 ? (
        <section className="section">
          <h2 className="section-title">{t("budget.items")}</h2>
          <div className="spend-list">
            {sorted.map((item) => (
              <article className="spend-row" key={item.id}>
                <span className="spend-label">{item.label}</span>
                <span className="spend-amount">
                  {formatMoneyMinor(item.currency, item.amountMinor, locale)}
                </span>
                <span className="spend-meta">
                  {t(`budgetCategory.${item.category}`)}
                  {". "}
                  {formatDateShort(item.spentAt, locale)}
                </span>
                <span className="spend-actions">
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={t("budget.editItem")}
                    disabled={busy}
                    onClick={() =>
                      setDraft({
                        id: item.id,
                        category: item.category,
                        label: item.label,
                        amount: minorToInput(item.amountMinor, item.currency),
                        currency: item.currency,
                        spentAt: item.spentAt.slice(0, 10),
                        note: item.note ?? "",
                      })
                    }
                  >
                    <Icon size={18}>
                      <IconPen />
                    </Icon>
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={t("budget.deleteItem")}
                    disabled={busy}
                    onClick={() => void remove(item.id)}
                  >
                    <Icon size={18}>
                      <IconTrash />
                    </Icon>
                  </button>
                </span>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
